async function requestJson(request, fallbackError) {
  try {
    const response = await request
    let data

    try {
      data = await response.json()
    } catch {
      return { ok: false, data: {}, error: fallbackError }
    }

    const hasObjectPayload = data !== null && typeof data === 'object' && !Array.isArray(data)
    if (!response.ok || !hasObjectPayload) {
      const error = hasObjectPayload && typeof data.error === 'string' && data.error
        ? data.error
        : fallbackError
      return { ok: false, data: hasObjectPayload ? data : {}, error }
    }

    return { ok: true, data, error: '' }
  } catch {
    return { ok: false, data: {}, error: fallbackError }
  }
}

function resolveNextAssignment(assignments, overview) {
  const rows = Array.isArray(assignments) ? assignments : []
  const continueId = overview?.continue_record?.assignment?.id
  const continueAssignment = rows.find(assignment => assignment.id === continueId)
  if (continueAssignment) return continueAssignment

  const upcomingId = overview?.upcoming_assignments?.[0]?.id
  return rows.find(assignment => assignment.id === upcomingId)
    || rows.find(assignment => ['not_started', 'in_progress'].includes(assignment.status))
    || rows[0] || null
}

function studentTaskOptions(assignments, participants = []) {
  const studies = participants.flatMap(participant => {
    const assignment = assignments.find(a => a.id === participant.assignment_id)
    return assignment ? [{ key: `study:${participant.id}`, assignment, participant,
      title: `${assignment.title} · ${participant.experiment_name || 'Writing study'}` }] : []
  })
  return [...studies, ...assignments.filter(a => !studies.some(s => s.assignment.id === a.id))
    .map(assignment => ({ key: `task:${assignment.id}`, assignment, participant: null, title: assignment.title }))]
}

function resolveStudentTask(assignments, overview, participants = [], selectedKey = '') {
  const options = studentTaskOptions(assignments, participants)
  const activeStudy = option => option.participant?.status === 'open' && option.participant.stage !== 'complete'
  const ordinaryNext = resolveNextAssignment(options.filter(o => !o.participant).map(o => o.assignment), overview)
  const ordinaryOption = options.find(o => !o.participant && o.assignment === ordinaryNext)
  const selected = options.find(o => o.key === selectedKey)
    || options.find(o => activeStudy(o) && o.participant.mode === 'live')
    || options.find(activeStudy)
    || (['not_started', 'in_progress'].includes(ordinaryNext?.status) ? ordinaryOption : null)
    || options.find(o => o.participant && o.participant.stage !== 'complete')
    || ordinaryOption || options[0]
  const { assignment = null, participant = null, key = '' } = selected || {}
  const entry = { assignment, participant, key, step: null, label: '', to: '', error: '' }
  if (!assignment) {
    if (participants.some(p => p.status === 'open' && p.stage !== 'complete')) entry.error = 'Your study task could not be loaded. Please retry or contact your teacher.'
    return entry
  }
  const workspace = `/student/task/${assignment.id}/workspace`
  if (!participant) {
    if (assignment.study && !assignment.study.can_write) {
      return {...entry, label:'Waiting for study setup', guidance:assignment.study.message,
        error:assignment.study.state==='enrolled' ? 'Your study progress could not be loaded. Please retry.' : ''}
    }
    return { ...entry, to: workspace, label: ['submitted', 'reviewed'].includes(assignment.status)
      ? 'View submission' : assignment.status === 'in_progress' ? 'Continue writing' : 'Start writing' }
  }
  const stages = {
    pre_questionnaire: [0, 'Continue pre-test'], pre_sample: [0, 'Continue pre-test'],
    writing: [1, 'Continue writing'], post_questionnaire: [2, 'Continue post-test'],
    post_self: [2, 'Continue post-test'], post_sample: [2, 'Continue post-test'],
    complete: [3, 'View submission'],
  }
  const [step, label] = stages[participant.stage] || [null, 'View progress']
  const writable = participant.status === 'open'
  return { ...entry, step, label: writable || participant.stage === 'complete' ? label : 'View progress',
    to: participant.stage === 'complete' || (writable && participant.stage === 'writing')
      ? workspace : `/student/experiments/${participant.id}` }
}

function canArchiveAssignment(assignment) {
  return assignment?.publication_status !== 'archived'
}

module.exports = {
  canArchiveAssignment,
  requestJson,
  resolveNextAssignment,
  resolveStudentTask,
  studentTaskOptions,
}
